import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import {
  chevronForward,
  chevronDown,
  checkmarkCircle,
  closeCircle,
  timeOutline,
  flaskOutline,
  pencilOutline,
  trashOutline,
  checkmarkOutline,
  codeSlashOutline,
  copyOutline,
  closeOutline,
  playCircleOutline
} from 'ionicons/icons';
import ModelSelect from '../ModelSelect';
import { getDefaultModelForAgent } from '../../../constants/aiModels';
import { useToast } from '../../../contexts/ToastContext';
import './TestCasesViewer.css';

function legacyToContent(test) {
  const lines = [];
  if (test.testObjective || test.description) {
    lines.push('WHAT THIS TESTS');
    lines.push(test.testObjective || test.description);
    lines.push('');
  }
  if (test.url || test.startUrl) {
    lines.push('START URL');
    lines.push(test.url || test.startUrl);
    lines.push('');
  }
  if (test.testCredentials && test.testCredentials.length > 0) {
    lines.push('TEST CREDENTIALS');
    test.testCredentials.forEach(cred => {
      lines.push(`• ${cred.field}: ${cred.value}`);
    });
    lines.push('');
  }
  if (test.testSteps && test.testSteps.length > 0) {
    lines.push('TEST STEPS');
    test.testSteps.forEach((step, i) => {
      lines.push(`${i + 1}. ${step}`);
    });
    lines.push('');
  } else if (test.steps && test.steps.length > 0) {
    lines.push('TEST STEPS');
    test.steps.forEach((step, i) => {
      const num = step.step || (i + 1);
      let line = `${num}. ${step.action?.toUpperCase() || 'ACTION'}`;
      if (step.target) line += ` on ${step.target}`;
      if (step.input) line += ` "${step.input}"`;
      lines.push(line);
    });
    lines.push('');
  }
  if (test.expectedResults && test.expectedResults.length > 0) {
    lines.push('EXPECTED RESULTS');
    test.expectedResults.forEach(result => {
      lines.push(`• ${result}`);
    });
  }
  return lines.join('\n').trim();
}

function normalizeTestCase(test) {
  return {
    id: test.id,
    title: test.title || 'Untitled Test',
    priority: test.priority || 'Medium',
    content: test.content || legacyToContent(test),
    status: test.status || 'pending',
    category: test.category || null,
    lastRunExecutionId: test.lastRunExecutionId || null,
    lastRunStatus: test.lastRunStatus || null,
    lastRunAt: test.lastRunAt || null
  };
}

function buildRunCommand(sourceIds, executionId, projectId) {
  const ids = Array.isArray(sourceIds) ? sourceIds.join(',') : sourceIds;
  return `zibby test --sources ${ids} --execution ${executionId} --sync --project ${projectId}`;
}

function copyToClipboard(text) {
  navigator.clipboard.writeText(text);
}

const CommandModal = ({ isOpen, baseCommand, aiAgent, defaultModel, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [model, setModel] = useState(() => defaultModel || getDefaultModelForAgent(aiAgent || 'claude'));

  useEffect(() => {
    if (isOpen) {
      setModel(defaultModel || getDefaultModelForAgent(aiAgent || 'claude'));
      setCopied(false);
    }
  }, [isOpen, defaultModel, aiAgent]);

  const fullCommand = baseCommand ? `${baseCommand} --model ${model}` : '';

  const handleCopy = () => {
    copyToClipboard(fullCommand);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="analyze-modal-overlay" onClick={onClose}>
      <div className="analyze-modal command-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Run Command</h2>
          <button className="close-btn" onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </button>
        </div>
        <div className="modal-content command-modal-content">
          <p className="command-modal-hint">
            Copy and paste this command into your terminal to run the test(s):
          </p>
          <div className="command-modal-model-select">
            <ModelSelect
              aiAgent={aiAgent || 'claude'}
              value={model}
              onChange={setModel}
              label="Model"
            />
          </div>
          <pre className="command-display" onClick={handleCopy} title="Click to copy">
            {fullCommand}
          </pre>
          <button
            className={`command-copy-btn ${copied ? 'command-copy-btn--copied' : ''}`}
            onClick={handleCopy}
          >
            <IonIcon icon={copied ? checkmarkOutline : copyOutline} />
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TestCasesViewer = ({ testCases, summary, defaultCollapsed = true, onChange, executionId, projectId, aiAgent, aiModel }) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [expandedTests, setExpandedTests] = useState(new Set());
  const [localTests, setLocalTests] = useState([]);
  const [commandModal, setCommandModal] = useState({ open: false, baseCommand: '' });

  useEffect(() => {
    if (testCases && testCases.length > 0) {
      setLocalTests(testCases.map(normalizeTestCase));
    }
  }, [testCases]);

  const totalCountsByPriority = useMemo(() => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0 };
    localTests.forEach(test => {
      const p = (test.priority || 'medium').toLowerCase();
      if (counts[p] !== undefined) counts[p]++;
    });
    return counts;
  }, [localTests]);

  const testsByPriority = useMemo(() => {
    const groups = { critical: [], high: [], medium: [], low: [] };
    localTests.forEach(test => {
      const p = (test.priority || 'medium').toLowerCase();
      if (groups[p]) groups[p].push(test);
    });
    return groups;
  }, [localTests]);

  const toggleTest = (testId) => {
    const newSet = new Set(expandedTests);
    if (newSet.has(testId)) newSet.delete(testId);
    else newSet.add(testId);
    setExpandedTests(newSet);
  };

  const handleUpdate = useCallback((testId, updates) => {
    setLocalTests(prev => {
      const next = prev.map(t => t.id === testId ? { ...t, ...updates } : t);
      onChange?.(next);
      return next;
    });
  }, [onChange]);

  const handleDelete = useCallback((testId) => {
    setLocalTests(prev => {
      const next = prev.filter(t => t.id !== testId);
      onChange?.(next);
      return next;
    });
    setExpandedTests(prev => {
      const newSet = new Set(prev);
      newSet.delete(testId);
      return newSet;
    });
  }, [onChange]);

  if (!localTests || localTests.length === 0) {
    return null;
  }

  const renderPriorityGroup = (priority, label) => {
    const tests = testsByPriority[priority];
    if (tests.length === 0) return null;
    return (
      <div className="priority-group" key={priority}>
        <h3 className={`priority-heading ${priority}`}>
          {label} ({totalCountsByPriority[priority]} {totalCountsByPriority[priority] === 1 ? 'test' : 'tests'})
        </h3>
        {tests.map(test => (
          <TestCaseItem
            key={test.id}
            test={test}
            expanded={expandedTests.has(test.id)}
            onToggle={() => toggleTest(test.id)}
            onUpdate={(updates) => handleUpdate(test.id, updates)}
            onDelete={() => handleDelete(test.id)}
            executionId={executionId}
            projectId={projectId}
            onShowCommand={(cmd) => setCommandModal({ open: true, baseCommand: cmd })}
            showToast={showToast}
            navigate={navigate}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="analysis-artifacts">
      <div className="artifact-section">
        <div className="artifact-header">
          <div className="artifact-header-left">
            <button
              className="content-collapse-toggle"
              onClick={() => setCollapsed(!collapsed)}
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              <IonIcon icon={collapsed ? chevronForward : chevronDown} />
            </button>
            <span className="test-cases-header-icon" aria-hidden="true">
              <IonIcon icon={flaskOutline} />
            </span>
            <h3
              className="test-cases-title clickable"
              onClick={() => setCollapsed(!collapsed)}
            >
              Test Cases
            </h3>
          </div>
          {executionId && projectId && (
            <div className="artifact-header-right">
              <button
                className="test-action-btn test-action-btn--copy"
                title="Copy zibby test command for all test cases"
                onClick={() => {
                  const ids = localTests.map(t => t.id);
                  setCommandModal({ open: true, baseCommand: buildRunCommand(ids, executionId, projectId) });
                }}
              >
                <IonIcon icon={codeSlashOutline} />
              </button>
            </div>
          )}
        </div>

        <CommandModal
          isOpen={commandModal.open}
          baseCommand={commandModal.baseCommand}
          aiAgent={aiAgent}
          defaultModel={aiModel}
          onClose={() => setCommandModal({ open: false, baseCommand: '' })}
        />

        {!collapsed && (
          <div className="test-cases-layout test-cases-layout--no-sidebar">
            <div className="test-main-content">
              {renderPriorityGroup('critical', 'CRITICAL')}
              {renderPriorityGroup('high', 'HIGH')}
              {renderPriorityGroup('medium', 'MEDIUM')}
              {renderPriorityGroup('low', 'LOW')}
              {localTests.length === 0 && (
                <div className="no-tests"><p>No tests to display.</p></div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TestCaseItem = ({ test, expanded, onToggle, onUpdate, onDelete, executionId, projectId, onShowCommand, showToast, navigate }) => {
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingContent, setEditingContent] = useState(false);
  const [titleDraft, setTitleDraft] = useState(test.title);
  const [contentDraft, setContentDraft] = useState(test.content);
  const titleInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    setTitleDraft(test.title);
    setContentDraft(test.content);
  }, [test.title, test.content]);

  useEffect(() => {
    if (editingTitle && titleInputRef.current) {
      titleInputRef.current.focus({ preventScroll: true });
      titleInputRef.current.select();
    }
  }, [editingTitle]);

  useEffect(() => {
    if (editingContent && textareaRef.current) {
      const el = textareaRef.current;
      el.style.height = el.scrollHeight + 'px';
      el.focus({ preventScroll: true });
    }
  }, [editingContent]);

  const handleTextareaInput = (e) => {
    setContentDraft(e.target.value);
    const el = e.target;
    el.style.overflow = 'hidden';
    el.style.height = el.scrollHeight + 'px';
  };

  const saveTitle = () => {
    const trimmed = titleDraft.trim();
    if (trimmed) {
      onUpdate({ title: trimmed });
      if (showToast) showToast('Saved', 'success', { position: 'bottom' });
    } else {
      setTitleDraft(test.title);
    }
    setEditingTitle(false);
  };

  const saveContent = () => {
    onUpdate({ content: contentDraft });
    setEditingContent(false);
    if (showToast) showToast('Saved', 'success', { position: 'bottom' });
  };

  const cancelContentEdit = () => {
    setContentDraft(test.content);
    setEditingContent(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed': return <IonIcon icon={checkmarkCircle} style={{ color: '#4caf50' }} />;
      case 'failed': return <IonIcon icon={closeCircle} style={{ color: '#f44336' }} />;
      default: return <IonIcon icon={timeOutline} style={{ color: '#999' }} />;
    }
  };

  return (
    <div className={`test-case-item priority-${test.priority} ${expanded ? 'expanded' : ''}`}>
      <div className="test-case-header" onClick={editingTitle ? undefined : onToggle}>
        <div className="test-header-left">
          {getStatusIcon(test.status)}
          {editingTitle ? (
            <input
              ref={titleInputRef}
              className="test-title-input"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveTitle();
                if (e.key === 'Escape') { setTitleDraft(test.title); setEditingTitle(false); }
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="test-title">{test.title}</div>
          )}
        </div>
        <div className="test-header-right">
          {test.lastRunExecutionId && projectId && (
            <button
              className={`tc-run-badge tc-run-badge--${test.lastRunStatus || 'unknown'}`}
              title={`Last run: ${test.lastRunStatus || 'unknown'}${test.lastRunAt ? ' — ' + new Date(test.lastRunAt).toLocaleString() : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/projects/${projectId}/runs/${test.lastRunExecutionId}`);
              }}
            >
              <IonIcon icon={test.lastRunStatus === 'passed' ? checkmarkCircle : test.lastRunStatus === 'failed' ? closeCircle : playCircleOutline} />
              <span>{test.lastRunStatus === 'passed' ? 'Passed' : test.lastRunStatus === 'failed' ? 'Failed' : 'Run'}</span>
            </button>
          )}
          {executionId && projectId && onShowCommand && (
            <button
              className="test-action-btn test-action-btn--copy"
              title="Show zibby test command"
              onClick={(e) => {
                e.stopPropagation();
                onShowCommand(buildRunCommand(test.id, executionId, projectId));
              }}
            >
              <IonIcon icon={codeSlashOutline} />
            </button>
          )}
          {!editingTitle && (
            <button
              className="test-action-btn"
              title="Edit title"
              onClick={(e) => { e.stopPropagation(); setEditingTitle(true); }}
            >
              <IonIcon icon={pencilOutline} />
            </button>
          )}
          <button
            className="test-action-btn test-action-btn--delete"
            title="Delete test case"
            onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete this test case?')) onDelete(); }}
          >
            <IonIcon icon={trashOutline} />
          </button>
          <IonIcon
            icon={expanded ? chevronDown : chevronForward}
            className="expand-icon"
          />
        </div>
      </div>

      {expanded && (
        <div className="test-case-details">
          <div className="content-edit-area" style={{ display: editingContent ? 'block' : 'none' }}>
            <textarea
              ref={textareaRef}
              className="test-content-textarea"
              value={contentDraft}
              onChange={handleTextareaInput}
            />
            <div className="content-edit-actions">
              <button className="content-edit-btn content-edit-btn--save" onClick={saveContent}>
                <IonIcon icon={checkmarkOutline} /> Save
              </button>
              <button className="content-edit-btn content-edit-btn--cancel" onClick={cancelContentEdit}>
                <IonIcon icon={closeOutline} /> Cancel
              </button>
            </div>
          </div>
          <div
            className="test-content-display"
            style={{ display: editingContent ? 'none' : 'block' }}
            onClick={() => setEditingContent(true)}
            title="Click to edit"
          >
            <pre className="test-content-pre">{test.content}</pre>
            <div className="content-edit-hint">
              <IonIcon icon={pencilOutline} /> Click to edit
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestCasesViewer;
